import { authManager } from './auth-manager.js';
import { trackPageView } from './analytics.js';
import { stopActiveScanner } from './views/scanner-view.js';
import { stopActiveProductScanner } from './views/scanner-product-view.js';
import { stopActiveAIScanner } from './views/scanner-ai-view.js';
import { stopActiveListaCotacaoScanner } from './views/lista-cotacao-view.js';

export class Router {
  constructor(routes) {
    this.routes = routes;
    this.container = document.getElementById('view-container');
    this.navItems = document.querySelectorAll('.nav-item');
    this.bottomNav = document.getElementById('bottom-nav');

    window.addEventListener('hashchange', () => this.handleRoute());
    window.addEventListener('authstatechanged', (e) => {
      if (!e.detail.authenticated && window.location.hash !== '#login') {
        window.location.hash = '#login';
      }
    });

    // Dynamic dual-function scanner button in bottom nav
    const navScanner = document.getElementById('nav-scanner');
    if (navScanner) {
      navScanner.addEventListener('click', (e) => {
        e.preventDefault();
        const currentHash = window.location.hash || '#home';
        const currentRoute = currentHash.split('/')[0].replace('#', '');

        // Persist origin page for precise return when on a valid page
        if (currentRoute !== 'scanner' && currentRoute !== 'scanner-ai' && currentRoute !== 'scanner-produto' && currentRoute !== 'lista-cotacao') {
          sessionStorage.setItem('scanner_origin_hash', currentHash);
          localStorage.setItem('scanner_origin_hash', currentHash);
        }

        const mode = localStorage.getItem('scanner_target_mode') || 'notas';
        if (mode === 'listas') {
          window.location.hash = '#scanner-ai';
        } else {
          window.location.hash = '#scanner';
        }
      });
    }

    // Lifecycle handling: release camera hardware on app background / minimize (vital for iOS PWA)
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        stopActiveScanner();
        stopActiveProductScanner();
        stopActiveAIScanner();
        stopActiveListaCotacaoScanner();
      }
    });
  }

  init() {
    this.handleRoute();
  }

  handleRoute() {
    const rawHash = window.location.hash;
    const defaultRoute = authManager.isAuthenticated() ? 'home' : 'landing';
    let routeName = rawHash ? rawHash.split('/')[0].replace('#', '') : defaultRoute;
    const param = rawHash ? (rawHash.split('/')[1] || null) : null;

    if (!routeName || routeName === '') {
      routeName = defaultRoute;
    }

    const isPublicRoute = routeName === 'landing' || routeName === 'login' || routeName === 'manual' || routeName === 'guia';

    // Route Guarding: If unauthenticated and accessing protected route, redirect to #landing
    if (!authManager.isAuthenticated() && !isPublicRoute) {
      window.location.hash = '#landing';
      return;
    }

    // If authenticated and accessing #login, redirect to #home
    if (authManager.isAuthenticated() && routeName === 'login') {
      window.location.hash = '#home';
      return;
    }

    // Admin Guarding: If accessing admin-only routes and not admin, redirect to #home
    if ((routeName === 'labs' || routeName === 'adminfeedbacks') && !authManager.isAdmin()) {
      window.location.hash = '#home';
      return;
    }

    // Update scanner target mode and origin ONLY when explicitly in Listas or Notas
    if (routeName === 'listas' || routeName === 'lista' || routeName === 'lista-cotacao') {
      localStorage.setItem('scanner_target_mode', 'listas');
      sessionStorage.setItem('scanner_origin_hash', rawHash || '#listas');
      localStorage.setItem('scanner_origin_hash', rawHash || '#listas');
      if (routeName === 'lista' || routeName === 'lista-cotacao') {
        sessionStorage.setItem('comparativo_origin_hash', rawHash);
      }
    } else if (routeName === 'notas' || routeName === 'nota') {
      localStorage.setItem('scanner_target_mode', 'notas');
      sessionStorage.setItem('scanner_origin_hash', rawHash || '#notas');
      localStorage.setItem('scanner_origin_hash', rawHash || '#notas');
    }

    if (!this.routes[routeName]) {
      routeName = defaultRoute;
    }

    // Stop active camera hardware stream whenever leaving scanner routes
    if (routeName !== 'scanner') {
      stopActiveScanner();
    }
    if (routeName !== 'scanner-produto') {
      stopActiveProductScanner();
    }
    if (routeName !== 'scanner-ai') {
      stopActiveAIScanner();
    }
    if (routeName !== 'lista-cotacao') {
      stopActiveListaCotacaoScanner();
    }

    // Show or hide bottom navbar based on route
    if (this.bottomNav) {
      if (routeName === 'login' || routeName === 'landing' || (!authManager.isAuthenticated() && (routeName === 'manual' || routeName === 'guia'))) {
        this.bottomNav.style.display = 'none';
      } else {
        this.bottomNav.style.display = 'flex';
      }
    }

    // Toggle body landing class for edge-to-edge layout
    document.body.classList.toggle('route-landing', routeName === 'landing');

    // Hide global header on landing, login, and unauthenticated manual public pages
    const appHeader = document.getElementById('app-header');
    if (appHeader) {
      if (routeName === 'landing' || routeName === 'login' || (!authManager.isAuthenticated() && (routeName === 'manual' || routeName === 'guia'))) {
        appHeader.style.display = 'none';
      } else {
        appHeader.style.display = 'block';
      }
    }

    // Update navbar active state
    this.navItems.forEach(item => {
      const tab = item.getAttribute('data-tab');
      if (
        tab === routeName ||
        ((routeName === 'nota' || routeName === 'notas') && tab === 'notas') ||
        ((routeName === 'lista' || routeName === 'listas' || routeName === 'dashboard') && tab === 'listas') ||
        ((routeName === 'scanner' || routeName === 'scanner-produto' || routeName === 'scanner-ai') && tab === 'scanner')
      ) {
        item.classList.add('active');
      } else {
        item.classList.remove('active');
      }
    });

    // Dynamic central scanner FAB icon, label & tooltip based on persisted target mode
    const navScanner = document.getElementById('nav-scanner');
    if (navScanner) {
      const fabBtn = navScanner.querySelector('.nav-fab-btn');
      const fabIcon = navScanner.querySelector('.nav-fab-icon');
      const fabLabel = navScanner.querySelector('#nav-fab-label');
      const targetMode = localStorage.getItem('scanner_target_mode') || 'notas';
      const isListMode = targetMode === 'listas';

      if (fabIcon) {
        fabIcon.textContent = isListMode ? 'barcode_scanner' : 'qr_code_scanner';
      }
      if (fabLabel) {
        fabLabel.textContent = isListMode ? 'Produtos' : 'NFC-e';
      }
      if (fabBtn) {
        fabBtn.classList.toggle('is-product-mode', isListMode);
      }
      navScanner.setAttribute('title', isListMode ? 'Escanear Produto' : 'Escanear NFC-e');
    }

    // Render view
    this.container.innerHTML = '';
    this.container.classList.remove('view-enter');
    // Force reflow
    void this.container.offsetWidth;
    this.container.classList.add('view-enter');

    const renderFn = this.routes[routeName];
    if (renderFn) {
      renderFn(this.container, param);
      trackPageView(rawHash || '#notas', `NF Tracker - ${routeName}`);
    }
  }

  navigate(hash) {
    if (window.location.hash === hash) {
      this.handleRoute();
    } else {
      window.location.hash = hash;
    }
  }
}
